/* =========================================================================
   Vidalite — Catalog hub
   /pages/catalog flow:
     - Click a category tile → grid filters to that category, shape drill
       swaps to the matching one (or hides if the category has no shapes).
     - Click a shape inside the drill → grid narrows further, scrolls down.
     - One card per product. Variants live on the PDP.
   ========================================================================= */

document.addEventListener('DOMContentLoaded', () => {

    /* Mobile menu toggle is handled globally in v3-main.js (event-delegated).
       The duplicate handler that used to live here double-toggled the drawer
       on the catalog hub, so it opened then immediately closed. Removed. */

    /* --- Architectural / Decorative style toggle ---------------------- */
    /* Pills above the tile grids. data-style-filter = "all" | "architectural"
       | "decorative". Click toggles which `[data-toggle-group]` blocks
       are visible AND filters the product grid below. */
    const styleToggleBtns = document.querySelectorAll('.cat-style-toggle__btn');
    const styleToggleGroups = document.querySelectorAll('[data-toggle-group]');
    let styleFilter = 'all';

    function applyStyleFilter() {
        // Hide entire group only if no tile inside it has a card matching
        // the active style. So a "decorative" group with at least one arch
        // product (e.g. Pendants → Egul/Cela) stays visible under the
        // Architectural toggle. Individual tiles get hidden too when their
        // category has zero products of the active style.
        styleToggleGroups.forEach(group => {
            let groupVisible = false;
            group.querySelectorAll('.cat-cat').forEach(tile => {
                const cat = tile.dataset.category;
                const hasMatch = (() => {
                    if (styleFilter === 'all') return true;
                    return Array.from(document.querySelectorAll('.cat-card')).some(card =>
                        card.dataset.category === cat && card.dataset.style === styleFilter
                    );
                })();
                tile.style.display = hasMatch ? '' : 'none';
                if (hasMatch) groupVisible = true;
            });
            group.style.display = groupVisible ? '' : 'none';
        });
    }

    styleToggleBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            styleFilter = btn.dataset.styleFilter || 'all';
            styleToggleBtns.forEach(b => b.classList.toggle('cat-style-toggle__btn--active', b === btn));
            applyStyleFilter();

            // Re-run the card filter so the grid below also narrows when
            // a category outside the active style was previously selected.
            if (typeof applyFilters === 'function') applyFilters();
        });
    });


    /* --- DOM refs ------------------------------------------------------ */
    const tiles           = document.querySelectorAll('.cat-cat');
    const shapesSection   = document.getElementById('shapes');
    const drillContainers = document.querySelectorAll('.cat-drill');
    const cards           = Array.from(document.querySelectorAll('.cat-card'));
    const empty           = document.getElementById('catEmpty');
    const gridTitle       = document.querySelector('[data-grid-title]');
    const gridResults     = document.querySelector('[data-grid-results]');

    if (!tiles.length || !cards.length) return;


    /* --- Filter drawer ------------------------------------------------ */
    /* Multi-select filters (finish, finish family, shape, price) layered
       on top of the single-select category/shape/style state above.
       Drawer options come from card datasets, so changes to product data
       refresh the filter UI on next page load with no Liquid edits. */
    const filterDrawer   = document.querySelector('[data-filter-drawer]');
    const filterBackdrop = document.querySelector('[data-filter-backdrop]');
    const filterToggle   = document.querySelector('[data-filter-toggle]');
    const filterCount    = document.querySelector('[data-filter-count]');
    const priceMinInput  = document.querySelector('[data-price-min]');
    const priceMaxInput  = document.querySelector('[data-price-max]');

    const drawerFilters = {
        family:  new Set(),
        finish:  new Set(),
        shape:   new Set(),
        priceMin: null,
        priceMax: null
    };

    const FILTER_LABELS = {
        family: {
            'classic':       'Plated Metal',
            'premium-metal': 'Premium Metal',
            'glass':         'Glass',
            'matte-color':   'Matte Color',
            'matte':         'Sand Matte'
        },
        finish: {}, // keys filled at runtime; pretty-printed from handle
        shape:  {
            'spot':            'Spot Light',
            '2head':           '2-Head Track',
            '3head':           '3-Head Track',
            '4head':           '4-Head Track',
            '5head':           '5-Head Track',
            '6head':           '6-Head Track',
            'round-trim':      'Round Trim',
            'round-trimless':  'Round Trimless',
            'round-gimbal':    'Round Gimbal',
            'square-trim':     'Square Trim',
            'square-trimless': 'Square Trimless'
        }
    };

    function prettyFromHandle(handle) {
        return handle
            .replace(/-/g, ' ')
            .replace(/\b\w/g, ch => ch.toUpperCase());
    }

    function buildFilterOptions() {
        ['family', 'finish', 'shape'].forEach(group => {
            const list = document.querySelector(`[data-filter-options="${group}"]`);
            if (!list) return;
            const counts = new Map();
            // shape is single-valued per product; family/finish are space-separated
            // lists across the product's variants. Split before counting so each
            // value gets its own pill and a card contributes once per token.
            const isMulti = (group === 'family' || group === 'finish');
            cards.forEach(card => {
                const raw = card.dataset[group];
                if (!raw) return;
                const keys = isMulti ? raw.split(' ').filter(Boolean) : [raw];
                const seen = new Set();
                keys.forEach(key => {
                    if (seen.has(key)) return;
                    seen.add(key);
                    counts.set(key, (counts.get(key) || 0) + 1);
                });
            });
            const sorted = Array.from(counts.entries()).sort((a, b) => {
                const la = (FILTER_LABELS[group][a[0]] || prettyFromHandle(a[0])).toLowerCase();
                const lb = (FILTER_LABELS[group][b[0]] || prettyFromHandle(b[0])).toLowerCase();
                return la.localeCompare(lb);
            });
            list.innerHTML = '';
            sorted.forEach(([key, n]) => {
                const li = document.createElement('li');
                const label = FILTER_LABELS[group][key] || prettyFromHandle(key);
                // Finish pills include a color swatch tied to the CSS variable
                // --finish-{handle}. Other groups (family/shape) stay text-only.
                const swatch = group === 'finish'
                    ? `<span class="cat-filter-pill__swatch" style="background: var(--finish-${key}, #c8c4bb);"></span>`
                    : '';
                li.innerHTML = `
                    <button type="button" class="cat-filter-pill" data-filter-input="${group}" data-value="${key}" aria-pressed="false">
                        ${swatch}
                        <span class="cat-filter-pill__label">${label}</span>
                        <span class="cat-filter-pill__count">${n}</span>
                    </button>`;
                list.appendChild(li);
            });
        });
    }

    function activeFilterCount() {
        let n = drawerFilters.family.size
              + drawerFilters.finish.size
              + drawerFilters.shape.size;
        if (drawerFilters.priceMin != null) n++;
        if (drawerFilters.priceMax != null) n++;
        return n;
    }

    function refreshFilterCountBadge() {
        if (!filterCount || !filterToggle) return;
        const n = activeFilterCount();
        if (n === 0) {
            filterCount.hidden = true;
            filterCount.textContent = '0';
            filterToggle.classList.remove('cat-filter-launcher--active');
        } else {
            filterCount.hidden = false;
            filterCount.textContent = String(n);
            filterToggle.classList.add('cat-filter-launcher--active');
        }
    }

    let drawerReleaseTrap = null;

    function openDrawer() {
        if (!filterDrawer) return;
        filterDrawer.hidden = false;
        filterBackdrop.hidden = false;
        filterDrawer.offsetHeight;
        filterDrawer.dataset.open = 'true';
        filterBackdrop.dataset.open = 'true';
        filterDrawer.setAttribute('aria-hidden', 'false');
        filterToggle?.setAttribute('aria-expanded', 'true');
        document.body.classList.add('cat-filter-open');
        if (window.VidaliteA11y) {
            drawerReleaseTrap = window.VidaliteA11y.trapFocus(filterDrawer, {
                onEscape: closeDrawer,
                restoreEl: filterToggle,
            });
        }
    }

    function closeDrawer() {
        if (!filterDrawer) return;
        filterDrawer.dataset.open = 'false';
        filterBackdrop.dataset.open = 'false';
        filterDrawer.setAttribute('aria-hidden', 'true');
        filterToggle?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('cat-filter-open');
        if (drawerReleaseTrap) { drawerReleaseTrap(); drawerReleaseTrap = null; }
        setTimeout(() => {
            if (filterDrawer.dataset.open === 'false') {
                filterDrawer.hidden = true;
                filterBackdrop.hidden = true;
            }
        }, 320);
    }

    function clearAllDrawerFilters() {
        drawerFilters.family.clear();
        drawerFilters.finish.clear();
        drawerFilters.shape.clear();
        drawerFilters.priceMin = null;
        drawerFilters.priceMax = null;
        document.querySelectorAll('[data-filter-input]').forEach(pill => {
            pill.setAttribute('aria-pressed', 'false');
        });
        if (priceMinInput) priceMinInput.value = '';
        if (priceMaxInput) priceMaxInput.value = '';
        refreshFilterCountBadge();
        applyFilters();
    }

    if (filterDrawer) {
        buildFilterOptions();

        filterToggle?.addEventListener('click', () => {
            const open = filterDrawer.dataset.open === 'true';
            if (open) closeDrawer(); else openDrawer();
        });
        filterBackdrop?.addEventListener('click', closeDrawer);
        document.querySelectorAll('[data-filter-close]').forEach(el => {
            el.addEventListener('click', closeDrawer);
        });
        document.addEventListener('keydown', e => {
            if (e.key === 'Escape' && filterDrawer.dataset.open === 'true') closeDrawer();
        });
        document.querySelector('[data-filter-clear]')?.addEventListener('click', clearAllDrawerFilters);

        // Pill click — toggle the aria-pressed state and the matching set.
        document.querySelectorAll('[data-filter-input]').forEach(pill => {
            pill.addEventListener('click', () => {
                const group = pill.dataset.filterInput;
                const val   = pill.dataset.value;
                const wasPressed = pill.getAttribute('aria-pressed') === 'true';
                if (wasPressed) {
                    pill.setAttribute('aria-pressed', 'false');
                    drawerFilters[group].delete(val);
                } else {
                    pill.setAttribute('aria-pressed', 'true');
                    drawerFilters[group].add(val);
                }
                refreshFilterCountBadge();
                applyFilters();
            });
        });

        const onPrice = () => {
            const lo = parseFloat(priceMinInput?.value);
            const hi = parseFloat(priceMaxInput?.value);
            drawerFilters.priceMin = isFinite(lo) && lo >= 0 ? lo : null;
            drawerFilters.priceMax = isFinite(hi) && hi >= 0 ? hi : null;
            refreshFilterCountBadge();
            applyFilters();
        };
        priceMinInput?.addEventListener('input', onPrice);
        priceMaxInput?.addEventListener('input', onPrice);
    }


    /* --- Category metadata -------------------------------------------- */
    /* Keys are collection handles (data-category on tile + card). drill
       is the drill panel id (or null if no shape picker). hasShapes
       drives whether shape filtering is offered. */
    const CATEGORY_META = {
        'track-lights':  { drill: 'track-lights',  countLabel: 'track lights',   hasShapes: true  },
        'downlights':    { drill: 'downlights',    countLabel: 'downlights',     hasShapes: true  },
        'flush-mounts':  { drill: null,            countLabel: 'flush mounts',   hasShapes: false },
        'pendants':      { drill: null,            countLabel: 'pendants',       hasShapes: false },
        'chandeliers':   { drill: null,            countLabel: 'chandeliers',    hasShapes: false },
        'wall-sconces':  { drill: null,            countLabel: 'wall sconces',   hasShapes: false },
        'vanity-lights': { drill: null,            countLabel: 'vanity lights',  hasShapes: false },
        'table-lamps':   { drill: null,            countLabel: 'table lamps',    hasShapes: false },
        'floor-lamps':   { drill: null,            countLabel: 'floor lamps',    hasShapes: false },
        'ceiling-fans':  { drill: null,            countLabel: 'ceiling fans',   hasShapes: false }
    };


    /* --- State --------------------------------------------------------- */
    const state = {
        category: 'track-lights',
        shape:    'all'
    };


    /* --- Filtering ---------------------------------------------------- */
    function applyFilters() {
        let visible = 0;
        const hasDrawer  = typeof drawerFilters !== 'undefined';
        const famSet     = hasDrawer ? drawerFilters.family : null;
        const finSet     = hasDrawer ? drawerFilters.finish : null;
        const shpSet     = hasDrawer ? drawerFilters.shape  : null;
        const priceMin   = hasDrawer ? drawerFilters.priceMin : null;
        const priceMax   = hasDrawer ? drawerFilters.priceMax : null;

        cards.forEach(card => {
            // Primary single-select filters (style toggle, tile click, shape click)
            let matches =
                (styleFilter    === 'all' || styleFilter    === card.dataset.style)    &&
                (state.category === 'all' || state.category === card.dataset.category) &&
                (state.shape    === 'all' || state.shape    === card.dataset.shape);

            // Drawer multi-select filters (any-of within group, all-of across groups).
            // Cards carry space-separated lists of every finish/family they cover
            // (one card per product, many variants), so a card matches if ANY of
            // its tokens intersects with the active filter set.
            if (matches && hasDrawer) {
                const anyIn = (csv, set) => (csv || '').split(' ').some(v => v && set.has(v));
                if (famSet.size > 0 && !anyIn(card.dataset.family, famSet)) matches = false;
                if (matches && finSet.size > 0 && !anyIn(card.dataset.finish, finSet)) matches = false;
                if (matches && shpSet.size > 0 && !shpSet.has(card.dataset.shape))     matches = false;
                if (matches && (priceMin != null || priceMax != null)) {
                    const price = parseFloat(card.dataset.price);
                    if (isFinite(price)) {
                        if (priceMin != null && price < priceMin) matches = false;
                        if (priceMax != null && price > priceMax) matches = false;
                    }
                }
            }

            card.classList.toggle('cat-card--hidden', !matches);
            if (matches) visible++;
        });

        if (empty) empty.hidden = visible !== 0;

        if (gridTitle) {
            const meta  = CATEGORY_META[state.category];
            const label = meta?.countLabel || 'products';
            if (state.shape !== 'all') {
                gridTitle.textContent = `${label} · ${prettyShape(state.shape)}`;
            } else {
                gridTitle.textContent = `All ${label}`;
            }
        }

        if (gridResults) {
            gridResults.textContent = visible === 1 ? '1 product' : `${visible} products`;
        }
    }

    function prettyShape(s) {
        const map = {
            'spot':            'Spot Light',
            '2head':           '2-Head Track',
            '3head':           '3-Head Track',
            '4head':           '4-Head Track',
            '5head':           '5-Head Track',
            '6head':           '6-Head Track',
            'round-trim':      'Round Trim',
            'round-trimless':  'Round Trimless',
            'round-gimbal':    'Round Gimbal',
            'square-trim':     'Square Trim',
            'square-trimless': 'Square Trimless'
        };
        return map[s] || s;
    }


    /* --- Category tile click ----------------------------------------- */
    function selectCategory(category, { scroll = true } = {}) {
        state.category = category;
        state.shape = 'all';

        tiles.forEach(b => b.classList.toggle('cat-cat--active', b.dataset.category === category));

        const meta = CATEGORY_META[category] || {};

        // Show the matching drill, hide the rest. Hide the section
        // entirely when the category has no drilldown.
        drillContainers.forEach(d => {
            d.hidden = d.dataset.drill !== meta.drill;
        });
        if (shapesSection) {
            shapesSection.style.display = meta.drill ? '' : 'none';
        }

        // Reset any active shape button.
        document.querySelectorAll('.cat-shape').forEach(b => b.classList.remove('cat-shape--active'));

        applyFilters();

        if (scroll) {
            const target = meta.drill ? shapesSection : document.getElementById('full-grid');
            setTimeout(() => target?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        }
    }

    tiles.forEach(btn => {
        btn.addEventListener('click', () => selectCategory(btn.dataset.category));
    });


    /* --- Shape click -------------------------------------------------- */
    document.querySelectorAll('.cat-shape').forEach(btn => {
        btn.addEventListener('click', () => {
            state.shape = btn.dataset.shape;

            document.querySelectorAll('.cat-shape').forEach(b => b.classList.remove('cat-shape--active'));
            btn.classList.add('cat-shape--active');

            applyFilters();

            setTimeout(() => document.getElementById('full-grid')?.scrollIntoView({
                behavior: 'smooth', block: 'start'
            }), 100);
        });
    });


    /* --- URL params (?category=, ?shape=, ?style=) -------------------- */
    const params = new URLSearchParams(window.location.search);

    // Style toggle deep-link — collection pages link out with ?style=…
    const urlStyle = params.get('style');
    if (urlStyle === 'architectural' || urlStyle === 'decorative') {
        styleFilter = urlStyle;
        styleToggleBtns.forEach(b => {
            b.classList.toggle('cat-style-toggle__btn--active', b.dataset.styleFilter === urlStyle);
        });
        applyStyleFilter();
    }

    const urlCat = params.get('category') || params.get('type');
    if (urlCat && CATEGORY_META[urlCat]) {
        selectCategory(urlCat, { scroll: false });
    } else {
        selectCategory(state.category, { scroll: false });
    }
    const urlShape = params.get('shape');
    if (urlShape) {
        document.querySelector(`.cat-shape[data-shape="${urlShape}"]`)?.click();
    }
});
